import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "preview", "previewImage", "previewMeta", "previewEmpty",
    "summaryList", "summaryToggle", "summaryCount",
    "headerCard", "socialCard",
    "headerBannerList", "socialBannerList",
    "submitButton"
  ]

  declare previewTarget: HTMLElement
  declare previewImageTarget: HTMLImageElement
  declare previewMetaTarget: HTMLElement
  declare hasPreviewEmptyTarget: boolean
  declare previewEmptyTarget: HTMLElement
  declare summaryListTarget: HTMLElement
  declare summaryToggleTarget: HTMLElement
  declare summaryCountTarget: HTMLElement
  declare submitButtonTarget: HTMLButtonElement
  declare hasHeaderCardTarget: boolean
  declare headerCardTarget: HTMLElement
  declare hasSocialCardTarget: boolean
  declare socialCardTarget: HTMLElement
  declare hasHeaderBannerListTarget: boolean
  declare headerBannerListTarget: HTMLElement
  declare hasSocialBannerListTarget: boolean
  declare socialBannerListTarget: HTMLElement

  private mode: string = "header"

  connect() {
    if (this.hasHeaderCardTarget) {
      this.applyMode()
    }
    this.update()
    // Use click listener on the whole controller element.
    // setTimeout(0) ensures we read checkbox state AFTER other controllers
    // (like select-all) have finished toggling checkboxes.
    this.element.addEventListener('click', this.scheduleUpdate)
    this.element.addEventListener('change', this.scheduleUpdate)
  }

  disconnect() {
    this.element.removeEventListener('click', this.scheduleUpdate)
    this.element.removeEventListener('change', this.scheduleUpdate)
  }

  scheduleUpdate = () => {
    setTimeout(() => this.update(), 0)
  }

  setMode(e: Event) {
    e.preventDefault()
    const link = (e.target as HTMLElement).closest<HTMLElement>('[data-mode]')
    if (!link) return
    const newMode = link.dataset.mode!
    if (newMode === this.mode) return

    this.mode = newMode
    this.deselectHiddenRadio()
    this.applyMode()
    this.update()
  }

  private deselectHiddenRadio() {
    if (!this.hasHeaderBannerListTarget) return
    const hiddenList = this.mode === 'header' ? this.socialBannerListTarget : this.headerBannerListTarget
    const radio = hiddenList.querySelector<HTMLInputElement>('input[type="radio"]:checked')
    if (radio) radio.checked = false
  }

  private applyMode() {
    // Toggle nav-link active state
    const links = this.element.querySelectorAll<HTMLElement>('.banner-mode-tabs [data-mode]')
    links.forEach(link => {
      if (link.dataset.mode === this.mode) {
        link.classList.add('active')
      } else {
        link.classList.remove('active')
      }
    })

    // Toggle banner lists
    if (this.hasHeaderBannerListTarget) {
      this.headerBannerListTarget.style.display = this.mode === 'header' ? '' : 'none'
      this.socialBannerListTarget.style.display = this.mode === 'social_media' ? '' : 'none'
    }

    // Toggle job cards
    if (this.hasHeaderCardTarget) {
      this.headerCardTarget.style.display = this.mode === 'header' ? '' : 'none'
      this.socialCardTarget.style.display = this.mode === 'social_media' ? '' : 'none'
    }
  }

  update() {
    this.updateBannerPreview()
    this.updateSummary()
    this.updateSubmitButton()
  }

  updateBannerPreview() {
    const selected = this.element.querySelector<HTMLInputElement>('input[name="banner_id"]:checked')
    if (selected) {
      const option = selected.closest('.banner-select-option')
      const img = option?.querySelector<HTMLImageElement>('.banner-select-thumb img')
      const meta = option?.querySelector('.banner-select-meta')
      if (img) {
        this.previewImageTarget.src = img.src
        this.previewImageTarget.style.display = ''
      } else {
        this.previewImageTarget.style.display = 'none'
      }
      if (meta) {
        this.previewMetaTarget.innerHTML = meta.innerHTML
      }
      this.previewTarget.style.display = ''
      if (this.hasPreviewEmptyTarget) this.previewEmptyTarget.style.display = 'none'
    } else {
      this.previewTarget.style.display = 'none'
      if (this.hasPreviewEmptyTarget) this.previewEmptyTarget.style.display = ''
    }
  }

  updateSummary() {
    // Only count checkboxes from the visible card (or all if no mode tabs)
    let scope: HTMLElement
    if (this.hasHeaderCardTarget) {
      scope = this.mode === 'header' ? this.headerCardTarget : this.socialCardTarget
    } else {
      scope = this.element as HTMLElement
    }
    const checked = scope.querySelectorAll<HTMLInputElement>(
      '.eb-card__body input[type="checkbox"]:checked'
    )
    const count = checked.length
    this.summaryCountTarget.textContent = `${count} ausgewählt`

    const labels: string[] = []
    checked.forEach(cb => {
      const label = cb.closest('.custom-control')?.querySelector('label')
      if (label) labels.push(label.textContent?.trim() || '')
    })

    if (labels.length > 0) {
      this.summaryListTarget.innerHTML = labels
        .map(l => `<div class="small text-truncate">${this.escapeHtml(l)}</div>`)
        .join('')
      this.summaryToggleTarget.style.display = ''
    } else {
      this.summaryListTarget.innerHTML = ''
      this.summaryToggleTarget.style.display = 'none'
      this.summaryListTarget.style.display = 'none'
    }
  }

  private updateSubmitButton() {
    const hasBanner = !!this.element.querySelector<HTMLInputElement>('input[name="banner_id"]:checked')

    let scope: HTMLElement
    if (this.hasHeaderCardTarget) {
      scope = this.mode === 'header' ? this.headerCardTarget : this.socialCardTarget
    } else {
      scope = this.element as HTMLElement
    }
    const hasSelection = scope.querySelectorAll<HTMLInputElement>(
      '.eb-card__body input[type="checkbox"]:checked'
    ).length > 0

    this.submitButtonTarget.disabled = !(hasBanner && hasSelection)
  }

  toggleSummary() {
    const isHidden = this.summaryListTarget.style.display === 'none'
    this.summaryListTarget.style.display = isHidden ? '' : 'none'
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
